import React, { useCallback, useEffect, useRef, useState } from 'react'

import { IoLogOutOutline, IoPersonOutline } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'

import ThemeSelector from './ThemeSelector'
import { useUser } from '../../../context'
import { RoleTone } from '../../constants'

import { PopoverAnchor, ProfileButton, ProfileMenuPanel } from './styles'

interface ProfileMenuProps {
  role?: RoleTone
}

const ProfileMenu = ({ role = 'student' }: ProfileMenuProps) => {
  const navigate = useNavigate()
  const { user, setOnEditionMode, signOut } = useUser()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback((returnFocus = true) => {
    setOpen(false)
    if (returnFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) close(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus()

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, open])

  const goToProfile = () => {
    setOnEditionMode(false)
    close(false)
    navigate('/perfil')
  }

  const logout = () => {
    signOut()
    navigate('/')
  }

  return (
    <PopoverAnchor ref={popoverRef}>
      <ProfileButton
        ref={triggerRef}
        type="button"
        $image={user?.image || '/assets/profile.png'}
        $role={role}
        aria-label="Abrir opciones de perfil"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />

      {open && (
        <ProfileMenuPanel
          ref={menuRef}
          $role={role}
          role="dialog"
          aria-label="Opciones de perfil"
        >
          <ThemeSelector role={role} />
          <button type="button" onClick={goToProfile}>
            <IoPersonOutline aria-hidden="true" /> Perfil
          </button>
          <button type="button" onClick={logout}>
            <IoLogOutOutline aria-hidden="true" /> Cerrar sesión
          </button>
        </ProfileMenuPanel>
      )}
    </PopoverAnchor>
  )
}

export default ProfileMenu
